/* eslint-disable no-console */
import {PlusOutlined} from '@ant-design/icons';
import {Collapse, Select, CollapseProps, Button, Flex, Spin, Modal} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import {ISelect} from '../../interface/ISelect';
import {handleIsComingSoonConditionally} from '../../pages/esgManager/constants/commonFunctions';
import {frameworkRoute} from '../../pages/esgManager/constants/frameworkRoutes';
import {FrameworkObj} from '../../pages/esgManager/constants/FrameWorksConst';
import {mapFrameworkPillarRoute} from '../../pages/esgManager/constants/returnQuestions';
import {inputType} from '../../pages/esgManager/interface/inputType';
import {sortYearOptions} from '../../pages/orgManager/Utils/InfraStructureProfile/constant/sorYear';
import {processFrameworkData} from '../../pages/reportBuilder/constants/processFrameworkData';
import {SearchItem} from '../../pages/reportBuilder/constants/ReportBuilderInterfce';
import {isTableFieldType} from '../../pages/reportBuilder/utils/common';
import {
  formatFrameworkFieldValue,
  normalizeFrameworkTableValue,
  resolveFrameworkTableCell
} from '../../pages/reportBuilder/utils/formatFrameworkFieldValue';
import {
  htmlToPlainTextPreview,
  isHtmlAnswerContent,
  resolveSidebarAnswerType
} from '../../pages/reportBuilder/utils/prepareFrameworkHtmlForInsert';
import countryJSON from '../../services/modules/JSON/country';
import {getFrameworkPillars} from '../../services/modules/ReportBuilder/ReportBuilderAPI';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import {useEditorStore} from '../../store/reportStore/editorStore';
import {matchesId} from '../../utils/companyCategorySubCategory';
import {yearSelect} from '../../utils/FormUtils/yearSelect';
import {hasAccessToReport} from '../../utils/SubscriptionHelper';
import InsertButton from '../Button/InsertButton';
import PilComp from '../DisplayComponent/PilComp';

const resolveSidebarQuestionLabel = (
  node: any,
  inheritedWhenEmpty?: string
): string => {
  const direct =
    String(node?.label ?? '').trim() ||
    String(node?.fieldLabel ?? '').trim() ||
    String(node?.question ?? '').trim();
  if (direct) return direct;

  return String(inheritedWhenEmpty ?? '').trim();
};

const collectFrameworkChildNodes = (node: any): any[] => {
  if (!node || typeof node !== 'object') return [];

  const out: any[] = [];
  const push = (arr: unknown) => {
    if (Array.isArray(arr) && arr.length > 0) out.push(...arr);
  };

  push(node.categories);
  push(node.subcategories);
  push(node.questions);
  push(node.children);

  return out;
};

type FormatDisplayValueFn = (
  value: any,
  answerType?: string,
  fieldType?: string
) => string;

type FormatAnswerObjectFn = (node: any) => Record<string, unknown> | null;

const formatSearchBlockAnswerPreview = (
  answer: any,
  formatDisplayValueFn: FormatDisplayValueFn
): string => {
  if (!answer) return '';
  if (answer.type === 'table') {
    const rowCount = Array.isArray(answer.rows) ? answer.rows.length : 0;
    const colCount = Array.isArray(answer.headers) ? answer.headers.length : 0;

    return rowCount || colCount
      ? `Table (${rowCount} rows × ${colCount} cols)`
      : 'Table';
  }
  if (isHtmlAnswerContent(answer)) {
    return htmlToPlainTextPreview(
      typeof answer.value === 'string' ? answer.value : ''
    );
  }
  if (answer.value !== undefined && answer.value !== null) {
    return formatDisplayValueFn(answer.value, answer.type);
  }

  return '';
};

/** Index the same processed tree the sidebar collapse renders (template + answers). */
const buildProcessedSearchIndex = (
  pillars: any[],
  formatDisplayValueFn: FormatDisplayValueFn,
  formatAnswerObjectFn: FormatAnswerObjectFn
): SearchItem[] => {
  const results: SearchItem[] = [];
  const seen = new Set<string>();

  const pushItem = (item: SearchItem) => {
    const dedupeKey = `${item.id}::${item.question}::${item.pathText}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    results.push(item);
  };

  const indexQuestionNode = (
    q: any,
    ancestors: string[],
    inheritedWhenEmpty?: string
  ) => {
    if (!q) return;

    const qid = String(q.questionId ?? q.id ?? '').trim();
    const displayQuestionText = resolveSidebarQuestionLabel(
      q,
      inheritedWhenEmpty
    );

    if (q?.type === 'heading-template') return;

    if (q?.type === 'field-group' && Array.isArray(q.blocks)) {
      const sectionLabel = displayQuestionText;
      const blockPath = sectionLabel
        ? [...ancestors, sectionLabel].filter(Boolean)
        : ancestors.filter(Boolean);
      const pathText = blockPath.join(' > ');
      const fb = sectionLabel;

      q.blocks.forEach((b: any, bi: number) => {
        if (b?.kind !== 'qa') return;
        const qText = String(b.question ?? '').trim() || fb;
        if (!qText) return;

        pushItem({
          id: qid ? `${qid}-block-${bi}` : `block-${results.length}`,
          question: qText,
          answerText: formatSearchBlockAnswerPreview(
            b.answer,
            formatDisplayValueFn
          ),
          answerObj: b.answer ?? null,
          pathText
        });
      });

      return;
    }

    const childQuestions = q?.children ?? [];
    const isAddMoreGroup =
      q?.fieldType === inputType.ADD_MORE_FIELDS || childQuestions.length > 0;

    if (isAddMoreGroup && childQuestions.length > 0) {
      const parentPath = displayQuestionText
        ? [...ancestors, displayQuestionText]
        : ancestors;
      childQuestions.forEach((child: any) => {
        indexQuestionNode(
          child,
          parentPath,
          q?.fieldType === inputType.ADD_MORE_FIELDS
            ? displayQuestionText
            : inheritedWhenEmpty
        );
      });

      return;
    }

    if (!displayQuestionText) return;

    const pathText = [...ancestors, displayQuestionText]
      .filter(Boolean)
      .join(' > ');
    pushItem({
      id: qid || `q-${results.length}`,
      question: displayQuestionText,
      answerText:
        q.value === undefined || q.value === null
          ? ''
          : formatDisplayValueFn(q.value, q.type, q.fieldType),
      answerObj: formatAnswerObjectFn(q),
      pathText
    });
  };

  (pillars ?? []).forEach((pillar) => {
    const pillarLabel = String(
      pillar?.title ?? pillar?.label ?? pillar?.pillarId ?? ''
    ).trim();
    const pillarPath = pillarLabel ? [pillarLabel] : [];

    (pillar?.categories ?? []).forEach((cat: any) => {
      const catLabel = String(cat?.label ?? '').trim();
      const catPath = catLabel ? [...pillarPath, catLabel] : pillarPath;

      (cat?.subcategories ?? []).forEach((sub: any) => {
        const subLabel = String(sub?.label ?? '').trim();
        const subPath = subLabel ? [...catPath, subLabel] : catPath;

        (sub?.questions ?? []).forEach((q: any) => {
          indexQuestionNode(q, subPath, sub?.label);
        });
      });

      (cat?.questions ?? []).forEach((q: any) => {
        indexQuestionNode(q, catPath, cat?.label);
      });
    });
  });

  return results;
};

/** Match _sidebar.scss $rb-collapse-* tokens for count pills */
const RB_PILL_GREY_BG = 'rgb(62, 92, 58)';
const RB_PILL_TEXT = '#ffffff';
const pillLabelStyle: React.CSSProperties = {color: RB_PILL_TEXT};

interface SidebarProps {
  onClose?: () => void;
}

export const Sidebar = ({onClose}: SidebarProps): React.ReactElement => {
  const companyCategory = useCompanyStore((s) => s.companyDetails?.category);
  // const companySubCategory = useCompanyStore(
  //   (s) => s.companyDetails?.subCategory
  // );

  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchItem[]>([]);
  const [mainData, setMainData] = useState<any>({});
  const [processedData, setProcessedData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [tableModalGroupNode, setTableModalGroupNode] = useState<any>(null);
  const [tableModalItems, setTableModalItems] = useState<any[]>([]);
  const [tableModalTitle, setTableModalTitle] = useState('');

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFramework, setSelectedFramework] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [collapseData, setCollapseData] = useState<any>([]);
  const yearOptions = yearSelect();
  const sortedYearOptions = useMemo(
    () => sortYearOptions([...yearOptions]),
    [yearOptions]
  );
  const defaultYear = sortedYearOptions[0]?.value ?? '';
  const selectedFrameworkConfig = useMemo(
    () => FrameworkObj?.find((item) => item.route === selectedFramework),
    [selectedFramework]
  );

  const frameworkSelectOptions = useMemo(
    () =>
      FrameworkObj.filter((item: any) => {
        if (item?.route == null) return false;
        if (item?.isComingSoon === true) return false;
        if (
          handleIsComingSoonConditionally(
            item.route as frameworkRoute,
            item?.isComingSoon
          )
        ) {
          return false;
        }

        return hasAccessToReport(item.route);
      }).map((item: any) => ({
        value: item.route as string,
        label: item.shortTitle
      })),
    [companyCategory]
  );

  const setFramework = (fw: string) => {
    setSelectedFramework(fw);
  };
  const setYear = (yr: string) => {
    setSelectedYear(yr);
  };

  const syncYearToSearchParams = (year: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('selectedYear', year);

      return next;
    });
  };

  useEffect(() => {
    const urlYear = searchParams.get('selectedYear');
    const isValidUrlYear =
      urlYear != null &&
      sortedYearOptions.some((option: ISelect) => option.value === urlYear);
    const resolvedYear = isValidUrlYear ? urlYear : defaultYear;

    if (!resolvedYear) return;

    setSelectedYear(resolvedYear);
    if (urlYear !== resolvedYear) {
      syncYearToSearchParams(resolvedYear);
    }
  }, []);

  const pillarsById = useMemo(() => {
    const list = processedData;
    if (!Array.isArray(list)) {
      return {};
    }

    return list.reduce(
      (acc: Record<string, unknown>, p: {pillarId?: string}) => {
        if (p?.pillarId) acc[p.pillarId] = p;

        return acc;
      },
      {}
    );
  }, [processedData]);

  useEffect(() => {
    if (!selectedFramework || !selectedYear) {
      setMainData({});
      setProcessedData([]);
      setCollapseData([]);

      return;
    }

    setIsLoading(true);
    setCollapseData([]);
    setProcessedData([]);

    getFrameworkPillars(selectedYear, selectedFramework)
      .then((res) => {
        setMainData(res ?? {});
        setIsLoading(false);
      })
      .catch(() => {
        setMainData({});
        setIsLoading(false);
      });

    Promise.all([countryJSON()])
      .then(() => {})
      .catch(() => {});
  }, [selectedFramework, selectedYear]);

  useEffect(() => {
    if (mainData) {
      const processed =
        processFrameworkData(mainData, selectedFramework, {
          includeUnansweredQuestions: false
        }) ?? [];
      setProcessedData(processed);
      const rawTimeline = selectedFrameworkConfig?.timeline ?? [];

      const timelineEntryPillarId = (item: any) => {
        const route = String(item?.routes ?? '');
        const routeParts = route.split('/');
        const routePillarPart = routeParts[1] ?? route;

        return mapFrameworkPillarRoute(selectedFramework, routePillarPart);
      };

      const timelineFiltered = Array.isArray(rawTimeline)
        ? rawTimeline.filter((item: any) => {
            if (item?.category == null || item?.category === undefined) {
              return true;
            }
            if (companyCategory == null || companyCategory === undefined) {
              const pid = timelineEntryPillarId(item);

              return processed.some((p: any) => p?.pillarId === pid);
            }

            return matchesId(companyCategory, item?.category);
          })
        : [];

      const pillarMap = processed.reduce(
        (acc: Record<string, any>, p: {pillarId?: string}) => {
          if (p?.pillarId) acc[p.pillarId] = p;

          return acc;
        },
        {}
      );

      const hasRenderablePillar = (pillarId: string) => {
        const pillar = pillarMap[pillarId] as {categories?: any[]} | undefined;
        const cats = pillar?.categories ?? [];

        return Array.isArray(cats) && cats.length > 0;
      };

      let data =
        Array.isArray(timelineFiltered) && timelineFiltered.length > 0
          ? timelineFiltered.map((item: any) => {
              const route = String(item?.routes ?? '');
              const routeParts = route.split('/');
              const routePillarPart = routeParts[1] ?? route;
              const pillarId = mapFrameworkPillarRoute(
                selectedFramework,
                routePillarPart
              );

              return {
                route: pillarId,
                title: item?.ptitle ?? item?.shortTitle ?? pillarId
              };
            })
          : processed
              .filter((item: any) => item?.pillarId)
              .map((item: any) => ({
                route: item.pillarId,
                title: item.title
              }));

      data = data.filter((row: {route: string}) =>
        hasRenderablePillar(row.route)
      );

      if (
        data.length === 0 &&
        processed.some(
          (p: any) => Array.isArray(p?.categories) && p.categories.length > 0
        )
      ) {
        data = processed
          .filter(
            (p: any) => Array.isArray(p?.categories) && p.categories.length > 0
          )
          .map((p: any) => ({
            route: p.pillarId,
            title: p.title ?? p.label ?? p.pillarId
          }));
      }

      setCollapseData(data);
    } else {
      setProcessedData([]);
    }
  }, [mainData, selectedFramework, selectedFrameworkConfig, companyCategory]);

  const formatDisplayValue = (
    value: any,
    answerType?: string,
    fieldType?: string
  ): string => {
    const resolvedType = resolveSidebarAnswerType(answerType, fieldType);
    if (resolvedType === 'html') {
      return htmlToPlainTextPreview(
        typeof value === 'string' ? value : String(value ?? '')
      );
    }

    if (
      typeof value === 'string' &&
      isHtmlAnswerContent({type: answerType, value})
    ) {
      return htmlToPlainTextPreview(value);
    }

    return formatFrameworkFieldValue(value);
  };

  const formatAnswerObject = (node: any) => {
    const answerType = node?.type ?? 'text';

    if (node.value === undefined || node.value === null) {
      if (node.type === 'table' && Array.isArray(node.columns)) {
        return {
          type: 'table',
          headers: node.columns,
          rows: []
        };
      }

      return {type: answerType, value: ''};
    }

    if (node.type === 'table') {
      const normalized = normalizeFrameworkTableValue(node.value);
      let headers = Array.isArray(node.columns) ? node.columns : [];
      const firstRow =
        Array.isArray(normalized) &&
        normalized.length > 0 &&
        normalized[0] &&
        typeof normalized[0] === 'object' &&
        !Array.isArray(normalized[0])
          ? (normalized[0] as Record<string, unknown>)
          : null;

      if (headers.length === 0 && firstRow) {
        const skipMeta = new Set(['_id', 'rowId', 'id', '__typename']);
        headers = Object.keys(firstRow)
          .filter((k) => !skipMeta.has(k))
          .map((k) => ({key: k, label: k}));
      }

      const rows = Array.isArray(normalized)
        ? normalized.map((row: any) => {
            if (Array.isArray(row)) {
              return row.map((v: any) => formatDisplayValue(v));
            }
            if (row && typeof row === 'object') {
              return headers.map((h: any) => {
                const colKey =
                  typeof h === 'string' ? h : String(h?.key ?? h?.label ?? '');
                const cell = resolveFrameworkTableCell(
                  row as Record<string, unknown>,
                  colKey
                );

                return formatDisplayValue(cell);
              });
            }

            return [];
          })
        : [];

      return {
        type: 'table',
        headers,
        rows
      };
    }

    const resolvedType = resolveSidebarAnswerType(node.type, node.fieldType);
    const rawValue =
      typeof node.value === 'string'
        ? node.value
        : node.value == null
          ? ''
          : String(node.value);

    // Preserve raw HTML whenever the value is HTML — even if the node's type
    // is not labelled html/wysiwyg — so it routes to the html-block renderer
    // instead of being stripped to a truncated plain-text preview.
    const isHtmlValue = isHtmlAnswerContent({
      type: node.type,
      fieldType: node.fieldType,
      value: rawValue
    });

    if (resolvedType === 'html' || isHtmlValue) {
      return {
        type: 'html',
        value: rawValue
      };
    }

    return {
      type: resolvedType,
      value: formatDisplayValue(node.value, node.type, node.fieldType)
    };
  };

  /** Flatten field-groups (and heading templates) so bulk insert matches per-field "Insert" behavior. */
  const expandFrameworkNodeForBulkInsert = (
    node: any,
    fallbackLabel: string
  ): any[] => {
    if (node?.type === 'heading-template') {
      const ht = String(node.headingText ?? node.label ?? '').trim();
      if (!ht) return [];

      return [
        {
          kind: 'heading',
          level: node.headingLevel ?? 'three',
          text: ht
        }
      ];
    }

    if (node?.type === 'field-group' && Array.isArray(node.blocks)) {
      const out: any[] = [];
      const fb = String(fallbackLabel ?? '').trim();

      for (const b of node.blocks) {
        if (b?.kind === 'heading' && String(b.text ?? '').trim()) {
          out.push({
            kind: 'heading',
            level: b.level ?? 'one',
            text: String(b.text).trim()
          });
        } else if (b?.kind === 'qa') {
          const qText = String(b.question ?? '').trim() || fb;

          out.push({
            kind: 'qa',
            question: qText || fb || '(Untitled)',
            answer: b.answer ?? null
          });
        }
      }

      return out;
    }

    const questionLabel = String(
      node?.label ?? node?.fieldLabel ?? node?.question ?? fallbackLabel ?? ''
    ).trim();
    if (!questionLabel) return [];

    return [
      {
        kind: 'qa',
        question: questionLabel,
        answer: formatAnswerObject(node)
      }
    ];
  };

  const insertAllQuestions = () => {
    if (!selectedFramework || !selectedYear) return;
    const topLevelItems = processedData;
    if (!Array.isArray(topLevelItems) || topLevelItems.length === 0) return;

    const items: any[] = [];
    topLevelItems.forEach((node: any) => {
      const leafNodes = getAllLeafNodes(node);
      leafNodes.forEach((n) => {
        items.push(
          ...expandFrameworkNodeForBulkInsert(n, n?.parentLabel ?? '')
        );
      });
    });
    if (items.length === 0) return;

    useEditorStore.getState().insertContent({
      type: 'bulk-qa',
      items
    });
  };

  const clearInsertedContent = () => {
    useEditorStore.getState().clearSidebarContent();
  };

  const insertQA = (question: string, answer: any) => {
    useEditorStore.getState().insertContent({
      type: 'qa',
      question,
      answer: answer || null
    });
  };

  const insertFieldGroup = (blocks: any[], e?: any) => {
    if (e) e.stopPropagation();
    if (!Array.isArray(blocks) || blocks.length === 0) return;

    useEditorStore.getState().insertContent({
      type: 'field-group',
      blocks
    });
  };

  const getAllLeafNodes = (node: any): any[] => {
    const merged = collectFrameworkChildNodes(node);
    if (merged.length > 0) {
      return merged.flatMap(getAllLeafNodes);
    }

    return [node];
  };

  // -------------------------
  // SEARCH (same processed tree as renderSections)
  // -------------------------

  const searchIndex = useMemo(
    () =>
      buildProcessedSearchIndex(
        processedData,
        formatDisplayValue,
        formatAnswerObject
      ),
    [processedData]
  );

  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      setSearchResults([]);
      setSearchLoading(false);

      return;
    }

    if (!selectedFramework || !selectedYear || !searchIndex.length) {
      setSearchResults([]);
      setSearchLoading(false);

      return;
    }

    setSearchLoading(true);
    let cancelled = false;

    const t = setTimeout(() => {
      const terms = q.split(/\s+/).filter(Boolean);
      const filtered = searchIndex.filter((item) => {
        const haystack =
          `${item.question} ${item.answerText} ${item.pathText}`.toLowerCase();

        return terms.every((term) => haystack.includes(term));
      });

      if (!cancelled) {
        setSearchResults(filtered.slice(0, 200));
        setSearchLoading(false);
      }
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [searchQuery, selectedFramework, selectedYear, searchIndex]);

  const insertBulkGroup = (groupNode: any, e?: any) => {
    if (e) e.stopPropagation();

    const leafNodes = getAllLeafNodes(groupNode);
    const fb = String(groupNode?.label ?? '').trim();
    const items = leafNodes.flatMap((n) =>
      expandFrameworkNodeForBulkInsert(n, fb)
    );
    if (items.length === 0) return;

    useEditorStore.getState().insertContent({
      type: 'bulk-qa',
      items
    });
  };

  const showTableModel = (groupNode: any, e?: any) => {
    if (e) e.stopPropagation();

    const leafNodes = getAllLeafNodes(groupNode);
    const items = leafNodes.map((n) => {
      return {
        question: n.label,
        answer: formatAnswerObject(n)
      };
    });

    setTableModalGroupNode(groupNode);
    setTableModalItems(items);
    setTableModalTitle('Table Preview');
    setIsTableModalOpen(true);
  };

  const closeTableModal = () => {
    setIsTableModalOpen(false);
    setTableModalGroupNode(null);
    setTableModalItems([]);
    setTableModalTitle('');
  };

  const handleTableInsert = () => {
    if (tableModalGroupNode?.__singleBlockInsert && tableModalItems[0]) {
      const row = tableModalItems[0];
      insertQA(row.question, row.answer);
      closeTableModal();

      return;
    }
    if (tableModalGroupNode) {
      insertBulkGroup(tableModalGroupNode);
    }
    closeTableModal();
  };

  const showTableModelForFieldGroupBlock = (block: any, e?: any) => {
    if (e) e.stopPropagation();
    if (block?.answer?.type !== 'table') return;

    setTableModalGroupNode({__singleBlockInsert: true});
    setTableModalItems([
      {
        question: String(block.question ?? '').trim() || 'Table',
        answer: block.answer
      }
    ]);
    setTableModalTitle(String(block.question ?? '').trim() || 'Table Preview');
    setIsTableModalOpen(true);
  };

  const previewTable = useMemo(() => {
    const tableItem = tableModalItems.find(
      (item) => item?.answer?.type === 'table'
    );
    if (!tableItem?.answer) return null;

    const answer = tableItem.answer;
    const headers = Array.isArray(answer.headers)
      ? answer.headers
          .map((header: any, index: number) => {
            if (typeof header === 'string') {
              return {key: header, label: header};
            }
            if (header && typeof header === 'object') {
              return {
                key: String(header.key ?? header.label ?? `col_${index}`),
                label: String(
                  header.label ?? header.key ?? `Column ${index + 1}`
                )
              };
            }

            return null;
          })
          .filter(Boolean)
      : [];

    const rows = Array.isArray(answer.rows) ? answer.rows : [];

    return {
      question: tableItem.question,
      headers,
      rows
    };
  }, [tableModalItems]);

  const renderQuestionRow = (
    q: any,
    keyPrefix: string,
    inheritedWhenEmpty?: string
  ) => {
    const reactKey = keyPrefix;
    const answerValue = formatDisplayValue(q.value, q.type, q.fieldType);
    const answerObj = formatAnswerObject(q);
    const childQuestions = q.children ?? [];
    const isAddMoreGroup =
      q?.fieldType === inputType.ADD_MORE_FIELDS || childQuestions.length > 0;
    const showNestedAddMoreChildren = childQuestions.length > 0;
    const isTableField = isTableFieldType(q);

    const displayQuestionText = resolveSidebarQuestionLabel(
      q,
      inheritedWhenEmpty
    );

    const headingLevelStyle = (
      level: string | undefined
    ): React.CSSProperties => {
      if (level === 'one') return {fontWeight: 700, fontSize: '0.95rem'};
      if (level === 'two') return {fontWeight: 650, fontSize: '0.9rem'};

      return {fontWeight: 600, fontSize: '0.85rem'};
    };

    if (q?.type === 'heading-template') {
      const ht = String(q.headingText ?? q.label ?? q.fieldLabel ?? '').trim();
      if (!ht) return null;

      const level = q.headingLevel ?? 'three';

      return (
        <div
          key={reactKey}
          className="fw-item-container sidebar-nested-question"
        >
          <Flex
            className="collapse-question-content"
            vertical={false}
            justify="space-between"
            align="center"
          >
            <div className="fw-content">
              <span className="fw-text" style={headingLevelStyle(level)}>
                {ht}
              </span>
            </div>
            <InsertButton
              onClick={(e: any) => {
                insertFieldGroup([{kind: 'heading', level, text: ht}], e);
              }}
            />
          </Flex>
        </div>
      );
    }

    if (q?.type === 'field-group') {
      const blocks = Array.isArray(q?.blocks) ? q.blocks : [];

      const formatBlockAnswerPreview = (answer: any): string => {
        if (!answer) return '';
        if (answer.type === 'table') {
          const rowCount = Array.isArray(answer.rows) ? answer.rows.length : 0;
          const colCount = Array.isArray(answer.headers)
            ? answer.headers.length
            : 0;

          return rowCount || colCount
            ? `Table (${rowCount} rows × ${colCount} cols)`
            : 'Table';
        }
        if (isHtmlAnswerContent(answer)) {
          return htmlToPlainTextPreview(
            typeof answer.value === 'string' ? answer.value : ''
          );
        }
        if (answer.value !== undefined && answer.value !== null) {
          return formatDisplayValue(answer.value, answer.type);
        }

        return '';
      };

      return (
        <div
          key={reactKey}
          className="fw-item-container sidebar-nested-question"
        >
          <Flex
            className="collapse-question-content"
            vertical={false}
            justify="space-between"
            align="center"
          >
            <div className="fw-content">
              <span className="fw-label">Section:</span>
              <span className="fw-text">{displayQuestionText}</span>
            </div>
            <Flex
              vertical={false}
              justify="space-between"
              align="center"
              gap={4}
            >
              <InsertButton
                text="Insert all"
                onClick={(e: any) => {
                  insertFieldGroup(blocks, e);
                }}
              />
            </Flex>
          </Flex>
          {blocks.length > 0 ? (
            <div className="sidebar-nested-inner">
              {blocks.map((b: any, bi: number) => {
                const bk = `${reactKey}-block-${bi}`;

                if (b?.kind === 'heading') {
                  const ht = String(b.text ?? '').trim();
                  if (!ht) return null;

                  return (
                    <div
                      key={bk}
                      className="fw-item-container sidebar-nested-question"
                    >
                      <Flex
                        className="collapse-question-content"
                        vertical={false}
                        justify="space-between"
                        align="center"
                      >
                        <div className="fw-content">
                          <span
                            className="fw-text"
                            style={headingLevelStyle(b.level)}
                          >
                            {ht}
                          </span>
                        </div>
                        <InsertButton
                          onClick={(e: any) => {
                            insertFieldGroup([b], e);
                          }}
                        />
                      </Flex>
                    </div>
                  );
                }

                if (b?.kind === 'qa') {
                  const qText =
                    String(b.question ?? '').trim() || '(no question label)';
                  const isTable = b?.answer?.type === 'table';

                  if (isTable) {
                    return (
                      <div
                        key={bk}
                        className="fw-item-container sidebar-nested-question"
                      >
                        <Flex
                          className="collapse-question-content"
                          vertical={false}
                          justify="space-between"
                          align="center"
                        >
                          <div className="fw-content">
                            <span className="fw-label">Q:</span>
                            <span className="fw-text">{qText}</span>
                          </div>
                        </Flex>
                        <div className="fw-box answer-box">
                          <div className="fw-content">
                            <span className="fw-label">A:</span>
                            <span
                              className="fw-text"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                              }}
                            >
                              <InsertButton
                                onClick={(e: any) => {
                                  insertFieldGroup([b], e);
                                }}
                              />
                              <InsertButton
                                text="Show Table"
                                onClick={(e: any) => {
                                  showTableModelForFieldGroupBlock(b, e);
                                }}
                              />
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  const answerPreview = formatBlockAnswerPreview(b.answer);

                  return (
                    <div
                      key={bk}
                      className="fw-item-container sidebar-nested-question"
                    >
                      <Flex
                        className="collapse-question-content"
                        vertical={false}
                        justify="space-between"
                        align="center"
                      >
                        <div className="fw-content">
                          <span className="fw-label">Q:</span>
                          <span className="fw-text">{qText}</span>
                        </div>
                        <InsertButton
                          onClick={(e: any) => {
                            insertFieldGroup([b], e);
                          }}
                        />
                      </Flex>
                      {answerPreview ? (
                        <div className="fw-box answer-box">
                          <div className="fw-content">
                            <span className="fw-label">A:</span>
                            <span className="fw-text">{answerPreview}</span>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                }

                return null;
              })}
            </div>
          ) : null}
        </div>
      );
    }

    if (isAddMoreGroup) {
      return (
        <div
          key={reactKey}
          className="fw-item-container sidebar-nested-question"
        >
          <Flex
            className="collapse-question-content"
            vertical={false}
            justify="space-between"
            align="center"
          >
            <div className="fw-content">
              <span className="fw-label">Q:</span>
              <span className="fw-text">{displayQuestionText}</span>
            </div>
            <Flex
              vertical={false}
              justify="space-between"
              align="center"
              gap={4}
            >
              <InsertButton
                onClick={(e: any) => {
                  insertBulkGroup(q, e);
                }}
              />
            </Flex>
          </Flex>
          {showNestedAddMoreChildren ? (
            <div className="sidebar-nested-inner">
              {childQuestions.map((child: any, i: number) =>
                renderQuestionRow(
                  child,
                  `${reactKey}-child-${i}`,
                  q?.fieldType === inputType.ADD_MORE_FIELDS
                    ? displayQuestionText
                    : undefined
                )
              )}
            </div>
          ) : null}
        </div>
      );
    }

    if (isTableField) {
      return (
        <div
          key={reactKey}
          className="fw-item-container sidebar-nested-question"
        >
          <Flex
            className="collapse-question-content"
            vertical={false}
            justify="space-between"
            align="center"
          >
            <div className="fw-content">
              <span className="fw-label">Q:</span>
              <span className="fw-text">{displayQuestionText}</span>
            </div>
          </Flex>

          <div className="fw-box answer-box">
            <div className="fw-content">
              <span className="fw-label">A:</span>
              <span
                className="fw-text"
                style={{display: 'flex', alignItems: 'center', gap: 4}}
              >
                <InsertButton
                  onClick={() => {
                    insertQA(displayQuestionText, answerObj);
                  }}
                />
                <InsertButton
                  text="Show Table"
                  onClick={(e: any) => {
                    showTableModel(q, e);
                  }}
                />
              </span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={reactKey} className="fw-item-container sidebar-nested-question">
        <Flex
          className="collapse-question-content"
          vertical={false}
          justify="space-between"
          align="center"
        >
          <div className="fw-content">
            <span className="fw-label">Q:</span>
            <span className="fw-text">{displayQuestionText}</span>
          </div>
          <Flex vertical={false} justify="space-between" align="center" gap={4}>
            <InsertButton
              onClick={() => {
                insertQA(displayQuestionText, answerObj);
              }}
            />
          </Flex>
        </Flex>
        {answerValue ? (
          <div className="fw-box answer-box">
            <div className="fw-content">
              <span className="fw-label">A:</span>
              <span className="fw-text">{answerValue}</span>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const buildSubcategoryCollapseItems = (
    subcategories: any[],
    parentKey: string
  ): CollapseProps['items'] =>
    (subcategories ?? []).map((sub) => {
      const sid = sub.subcategoryId ?? sub.id ?? `${parentKey}-sub`;
      const questions = sub.questions ?? [];
      const qCount = getAllLeafNodes(sub).length;
      const headerLabel = sub?.label ?? '';

      return {
        key: String(sid),
        label: (
          <Flex
            className="sidebar-collapse-label"
            vertical={false}
            justify="space-between"
            align="center"
          >
            <span>{sub.label}</span>
            <Flex vertical gap={4} align="flex-end">
              <Flex
                vertical={false}
                justify="space-between"
                align="center"
                gap={4}
              >
                <PilComp
                  value={`${qCount}`}
                  color={RB_PILL_GREY_BG}
                  style={pillLabelStyle}
                />
                <InsertButton
                  onClick={(e: any) => {
                    insertBulkGroup(sub, e);
                  }}
                />
              </Flex>
            </Flex>
          </Flex>
        ),
        children: (
          <div className="sidebar-nested-inner">
            {questions.map((q: any, i: number) =>
              renderQuestionRow({...q, parentLabel: headerLabel}, `${sid}-${i}`)
            )}
          </div>
        )
      };
    });

  const buildCategoryCollapseItems = (
    categories: any[]
  ): CollapseProps['items'] =>
    (categories ?? [])?.map((cat) => {
      const cid = cat.categoryId;
      const subs = cat.subcategories ?? [];
      const directQs = cat.questions ?? [];
      const catCount = getAllLeafNodes(cat).length;

      let inner: React.ReactNode;
      if (subs.length > 0) {
        inner = (
          <Collapse
            size="small"
            bordered={false}
            className="reportBuilder-collapse rb-collapse-htne"
            items={buildSubcategoryCollapseItems(subs, String(cid))}
          />
        );
      } else if (directQs.length > 0) {
        inner = (
          <div className="sidebar-nested-inner">
            {directQs.map((q: any, i: number) =>
              renderQuestionRow(q, `${cid}-q-${i}`)
            )}
          </div>
        );
      } else {
        inner = (
          <div className="no-data-message sidebar-nested-inner">No items</div>
        );
      }

      return {
        key: String(cid),
        label: (
          <Flex
            className="sidebar-collapse-label"
            vertical={false}
            justify="space-between"
            align="center"
          >
            <span>{cat.label}</span>
            <Flex
              vertical={false}
              justify="space-between"
              align="center"
              gap={4}
            >
              <PilComp
                value={`${catCount}`}
                color={RB_PILL_GREY_BG}
                style={pillLabelStyle}
              />
              <InsertButton
                onClick={(e: any) => {
                  insertBulkGroup(cat, e);
                }}
              />
            </Flex>
          </Flex>
        ),
        children: <div className="sidebar-category-body">{inner}</div>
      };
    });

  const renderPillarBody = (pillarId: string) => {
    const pillar = pillarsById[pillarId] as
      | {categories?: any[]; label?: string}
      | undefined;
    if (!pillar) {
      return (
        <div className="no-data-message">
          No report data for this pillar. Check year and framework.
        </div>
      );
    }

    const categories = pillar.categories ?? [];
    if (categories.length === 0) {
      return <div className="no-data-message">No sections in this pillar.</div>;
    }

    return (
      <Collapse
        size="small"
        bordered={false}
        className="reportBuilder-collapse rb-collapse-inner"
        items={buildCategoryCollapseItems(categories)}
      />
    );
  };

  // -------------------------
  // RENDER SECTIONS
  // -------------------------

  const renderSections = () => {
    const isSearching = searchQuery.trim().length > 0;
    if (isSearching) {
      if (searchLoading) {
        return <div className="no-data-message">Searching…</div>;
      }
      if (!searchResults.length) {
        return <div className="no-results">No results</div>;
      }

      return (
        <div className="search-results">
          {searchResults.map((r) => (
            <div
              key={`${r.id}::${r.pathText}`}
              className="fw-item-container sidebar-nested-question"
            >
              <Flex
                className="collapse-question-content"
                vertical={false}
                justify="space-between"
                align="center"
              >
                <div className="fw-content">
                  <span className="fw-label">Q:</span>
                  <span className="fw-text">{r.question}</span>
                </div>
                <InsertButton
                  onClick={() => {
                    insertQA(r.question, r.answerObj);
                  }}
                />
              </Flex>
              {r.pathText ? <div className="fw-path">{r.pathText}</div> : null}
              {r.answerText ? (
                <div className="fw-box answer-box">
                  <div className="fw-content">
                    <span className="fw-label">A:</span>
                    <span className="fw-text">{r.answerText}</span>
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      );
    }

    if (!selectedYear || !selectedFramework) {
      return <div className="no-data-message"></div>;
    }

    if (!collapseData?.length) {
      return <div className="no-data-message">No data for this framework.</div>;
    }

    const outerItems: CollapseProps['items'] = collapseData?.map(
      (item: {route: string; title: string}) => {
        const pillarId = item.route;
        const pillar = pillarsById[pillarId] as {label?: string} | undefined;
        const totalQ = pillar ? getAllLeafNodes(pillar).length : 0;

        return {
          key: pillarId,
          label: (
            <Flex
              className="sidebar-collapse-label sidebar-pillar-label"
              vertical={false}
              justify="space-between"
              align="center"
            >
              <span>{item.title}</span>
              <Flex
                vertical={false}
                justify="space-between"
                align="center"
                gap={4}
              >
                <PilComp
                  value={`${totalQ}`}
                  color={RB_PILL_GREY_BG}
                  style={pillLabelStyle}
                />
                <InsertButton
                  onClick={(e: any) => {
                    insertBulkGroup(pillar, e);
                  }}
                />
              </Flex>
            </Flex>
          ),
          children: renderPillarBody(pillarId)
        };
      }
    );

    const questionsCount =
      processedData?.reduce((acc: number, pillar: any) => {
        if (!pillar) return acc;

        return acc + getAllLeafNodes(pillar).length;
      }, 0) ?? 0;

    return (
      <Flex vertical style={{margin: '10px'}} gap={10}>
        <Flex className="module-summary">
          <span className="module-summary-text">
            {selectedFramework.toUpperCase()}
          </span>
          <Flex
            className="module-summary-count"
            vertical={false}
            justify="space-between"
            align="center"
            gap={4}
          >
            <span>{processedData?.length} Modules</span>
            <span>{questionsCount} Questions</span>
          </Flex>
        </Flex>
        <Collapse
          className="reportBuilder-collapse rb-collapse-outer"
          items={outerItems}
          destroyInactivePanel={false}
        />
      </Flex>
    );
  };

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <h3>Content Elements</h3>
        {onClose && (
          <button
            style={{position: 'absolute', right: '10px', top: '10px'}}
            className="toolbar-btn"
            onClick={onClose}
          >
            ✕
          </button>
        )}
      </div>

      <div className="sidebar-controls">
        <div className="control-group">
          <label>Year</label>
          <Select
            value={selectedYear || undefined}
            onChange={(value) => {
              setYear(value);
              syncYearToSearchParams(value);
            }}
            placeholder="Please select year"
            options={sortedYearOptions}
          />
        </div>

        <div className="control-group">
          <label>Framework</label>
          <Select
            value={selectedFramework || undefined}
            onChange={(e) => {
              setFramework(e);
            }}
            disabled={!selectedYear}
            placeholder="Please select framework"
            options={frameworkSelectOptions}
          />
        </div>

        <div className="search-bar-container">
          <input
            type="text"
            placeholder="Search questions..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            disabled={
              !selectedFramework ||
              !selectedYear ||
              Boolean(selectedFrameworkConfig?.isComingSoon)
            }
          />
        </div>

        <Button
          // type="default"
          // style={{background: '#3e5c3a', color: '#fff'}}
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            insertAllQuestions();
          }}
          disabled={!selectedFramework || !selectedYear}
        >
          Insert All Questions
        </Button>
      </div>

      <div className="sidebar-content show-scrollbar">
        {isLoading ? (
          <Flex className="sidebar-loader" align="center" justify="center">
            <Spin size="large" />
          </Flex>
        ) : (
          renderSections()
        )}
      </div>

      <Modal
        title={tableModalTitle}
        open={isTableModalOpen}
        onCancel={closeTableModal}
        width={900}
        footer={[
          <Button key="close" onClick={closeTableModal}>
            Close
          </Button>,
          <Button key="insert" type="primary" onClick={handleTableInsert}>
            Insert
          </Button>
        ]}
      >
        {previewTable ? (
          <div>
            {previewTable.question ? (
              <div style={{fontWeight: 600, marginBottom: 8}}>
                {previewTable.question}
              </div>
            ) : null}
            <div style={{overflowX: 'auto'}}>
              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <thead>
                  <tr>
                    {previewTable.headers.map((header: any, index: number) => (
                      <th
                        key={`${header.key}-${index}`}
                        style={{
                          border: '1px solid #d9d9d9',
                          padding: '8px',
                          textAlign: 'left',
                          background: '#fafafa'
                        }}
                      >
                        {header.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewTable.rows.map((row: any, rowIndex: number) => (
                    <tr key={`row-${rowIndex}`}>
                      {previewTable.headers.map(
                        (header: any, colIndex: number) => {
                          const value = Array.isArray(row)
                            ? row[colIndex]
                            : row && typeof row === 'object'
                              ? row[header.key]
                              : '';

                          return (
                            <td
                              key={`cell-${rowIndex}-${colIndex}`}
                              style={{
                                border: '1px solid #d9d9d9',
                                padding: '8px'
                              }}
                            >
                              {String(value ?? '')}
                            </td>
                          );
                        }
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div>No table data available.</div>
        )}
      </Modal>

      <div className="sidebar-footer">
        <button className="clear-content-btn" onClick={clearInsertedContent}>
          CLEAR INSERTED CONTENT
        </button>
      </div>
    </div>
  );
};
